import { apiRequest, ApiError } from '@/lib/api/client';
import {
  clearAuthSession,
  getAuthToken,
  getAuthUser,
  setAuthSession,
  setAuthUser,
  setRememberedLoginId,
} from '@/lib/api/token-store';
import { setProfileHasPin, upsertProfileFromUser } from '@/lib/stores/profile-roster-store';
import { preloadUserSettingsForLogin } from '@/lib/stores/user-settings-sync';
import { destroyWorkspace } from '@/lib/stores/workspace-store';

function authHeaders(headers = {}) {
  const token = String(getAuthToken() || '').trim();
  if (!token) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${token}`,
    ...headers,
  };
}

// Shared tail of every successful login (password or PIN). The roster upsert runs BEFORE
// setAuthSession so the profile list is already consistent when the single auth-state event
// fires; setAuthSession replaces token+user atomically (one event, no mixed-identity frame).
// The account-settings preload also runs BEFORE setAuthSession: workspace panes mount off
// that event, so read-once consumers (calendar/messages useState initializers) must find
// the server values already installed. Bounded and non-fatal — login never fails or hangs
// because settings couldn't load.
async function applySession(payload, loginId) {
  const token = typeof payload?.token === 'string' ? payload.token.trim() : '';
  if (!token) {
    throw new ApiError('token missing in login response', 500, payload);
  }

  const user = payload?.user || null;
  upsertProfileFromUser(user);
  await preloadUserSettingsForLogin({ uid: user?.id, token });
  setAuthSession({ token, user });
  setRememberedLoginId(loginId);

  return {
    token,
    user,
    loginId,
  };
}

export async function loginWithCredentials({ loginId, password }) {
  const normalizedLoginId = String(loginId || '').trim().toLowerCase();
  const normalizedPassword = String(password || '').trim();

  if (!normalizedLoginId || !normalizedPassword) {
    throw new ApiError('loginId and password are required', 400);
  }

  const payload = await apiRequest('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      loginId: normalizedLoginId,
      password: normalizedPassword,
    }),
  });

  return applySession(payload, normalizedLoginId);
}

/**
 * Quick profile switch: exchange loginId + PIN for a fresh (shorter-lived) session.
 * 401 = wrong/unknown PIN, 429 = locked out (payload carries retryAfterSeconds); anything
 * else (e.g. 404 from an old backend without the endpoint) means PIN sign-in is unavailable
 * and callers should fall back to the password path.
 */
export async function loginWithPin({ loginId, pin }) {
  const normalizedLoginId = String(loginId || '').trim().toLowerCase();
  const normalizedPin = String(pin || '').trim();

  if (!normalizedLoginId || !normalizedPin) {
    throw new ApiError('loginId and pin are required', 400);
  }

  const payload = await apiRequest('/api/login/pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      loginId: normalizedLoginId,
      pin: normalizedPin,
    }),
  });

  return applySession(payload, normalizedLoginId);
}

// PIN set/clear both require password re-auth server-side (blocks PIN implantation on an
// unlocked colleague session). On success the roster entry and the cached auth user are
// updated so hasPin-driven UI (switch dialogs, setup prompts) reflects the change at once.
function syncHasPin(hasPin) {
  const user = getAuthUser();
  if (!user) return;
  setProfileHasPin(user.id, hasPin);
  setAuthUser({ ...user, hasPin });
}

export async function setCurrentUserPin({ pin, password }) {
  const payload = await apiRequest('/api/users/me/pin', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      pin: String(pin || '').trim(),
      password: String(password || '').trim(),
    }),
  });
  syncHasPin(true);
  return payload;
}

export async function clearCurrentUserPin({ password }) {
  const payload = await apiRequest('/api/users/me/pin', {
    method: 'DELETE',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      password: String(password || '').trim(),
    }),
  });
  syncHasPin(false);
  return payload;
}

export async function changeCurrentUserPassword({ currentPassword, newPassword }) {
  return apiRequest('/api/users/me/password', {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      currentPassword: String(currentPassword || ''),
      newPassword: String(newPassword || ''),
    }),
  });
}

export async function logout() {
  // Capture the identity before the request — clearAuthSession would erase it.
  const userId = String(getAuthUser()?.id || '').trim();
  try {
    await apiRequest('/api/logout', {
      method: 'POST',
    });
  } finally {
    // Voluntary logout is a privacy boundary: the user's frozen workspace is destroyed
    // BEFORE the session clears (401 expiry never comes through here — it only freezes).
    if (userId) {
      destroyWorkspace(userId);
    }
    // One atomic event; the roster is untouched — the profile stays on the switcher, dormant.
    clearAuthSession();
  }
}
